import { catchError, map, Observable, of } from 'rxjs'
import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'

import { IResponse } from '@data/interfaces/iresponseArray.interface'
import { environment } from '@env/environment'

/**
 * Respuesta extendida del wrapper IResponse para propagar el `code`
 * machine-readable del backend (ej. `CIEC_NOT_STORED` post-ADR-0032). El front
 * lo usa para ramificar UX sin parsear el `msg`.
 */
export interface IResponseWithCode extends IResponse {
  code?: string
}

/**
 * ApiCustomerVerificationService (ADR-0031 + ADR-0032).
 *
 * Cliente HTTP de los endpoints CIEC del cliente.
 * Backend: `verification/controllers/client-ciec.controller.ts` (vive en
 * VerificationModule para evitar el ciclo SmsModule↔ClientsModule↔VerificationModule
 * que el ADR documenta) + `ciec-request/controllers/ciec-request.controller.ts`
 * (magic-link backoffice, ADR-0032).
 *
 *   GET    /clients/:id/verification                          → última verificación + isExpired
 *   GET    /clients/:id/verifications                         → historial completo (no usado en UI v1)
 *   POST   /clients/:id/verify-ciec                           → revalidación síncrona (backoffice)
 *   POST   /clients/:id/ciec-request                          → generar magic link + mandar correo
 *   GET    /clients/:id/ciec-requests                         → historial de magic links
 *   DELETE /clients/:id/ciec-requests/:requestId              → cancelar magic link pendiente
 *   POST   /clients/:id/ciec-requests/:requestId/resend       → regenerar + reenviar
 *
 * El interceptor de auth global inyecta el Bearer token; este service no
 * necesita configuración adicional.
 */
@Injectable({ providedIn: 'root' })
export class ApiCustomerVerificationService {
  private http = inject(HttpClient)

  /**
   * GET /clients/:id/verification — última verificación del cliente con flag
   * `isExpired`. La respuesta sigue el wrapper `IResponse` estándar
   * (`{ error, msg, data: { verification, isExpired } }`).
   */
  getLatest(clientId: string): Observable<IResponse> {
    const response: IResponse = { error: true, msg: '', data: null }
    return this.http.get<IResponse>(`${environment.apiUrl}clients/${clientId}/verification`).pipe(
      map((r) => {
        response.data = r.data ?? r
        response.msg = r.msg ?? ''
        response.error = r.error ?? false
        return response
      }),
      catchError((e) => {
        response.msg = e?.error?.msg ?? 'No se pudo cargar el análisis fiscal'
        response.data = null
        response.error = true
        return of(response)
      }),
    )
  }

  /**
   * GET verification/providers/ekatena/report — ¿hay ya un análisis de Ekatena para este RFC?
   *
   * Es una pregunta barata: el filtro por RFC es server-side y **no consume créditos**, así que se
   * puede hacer al abrir la ficha sin comprometer nada. Devuelve `null` cuando no hay ninguno, que
   * es un resultado normal y no un error.
   */
  buscarReporteEkatena(rfc: string): Observable<any | null> {
    if (!rfc) return of(null)
    return this.http.get<IResponse>(`${environment.apiUrl}verification/providers/ekatena/report`, { params: { rfc } }).pipe(
      map((r) => (r as any)?.data ?? null),
      //* Que el proveedor no responda no puede romper la ficha: se comporta como «no hay nada».
      catchError(() => of(null)),
    )
  }

  /**
   * POST /clients/:id/import-ekatena — trae el análisis que ya existe en Ekatena.
   *
   * Aditivo: crea una verificación nueva sin tocar la anterior. No consume créditos —el reporte ya
   * lo pagó quien lo corrió en el panel del proveedor—.
   */
  importarEkatena(clientId: string): Observable<IResponseWithCode> {
    const response: IResponseWithCode = { error: true, msg: '', data: null }
    return this.http.post<IResponse>(`${environment.apiUrl}clients/${clientId}/import-ekatena`, {}).pipe(
      map((r) => {
        response.data = r.data ?? r
        response.msg = r.msg ?? ''
        response.error = r.error ?? false
        return response
      }),
      catchError((e) => {
        response.msg = e?.error?.message ?? e?.error?.msg ?? 'No se pudo importar el análisis de Ekatena'
        response.code = e?.error?.code
        response.data = null
        response.error = true
        return of(response)
      }),
    )
  }

  /**
   * POST /clients/:id/verify-ciec — dispara revalidación síncrona (3-10s con
   * Syntage). Devuelve `{ verification, result }` con la ClientVerification
   * recién persistida.
   *
   * ADR-0032: si el cliente no tiene CIEC guardada (caso histórico o cliente
   * nuevo que nunca verificó), el backend responde 400 con `code='CIEC_NOT_STORED'`.
   * El customer-detail mapea ese código para abrir el modal de magic link.
   *
   * `ciec` opcional: si un usuario interno la ingresa a mano, se manda en el body y
   * el backend la usa; solo la guarda en bóveda si el SAT la valida (B-075). Sin `ciec` →
   * reusa la CIEC guardada. Un fallo del análisis llega como 200 con `result.success=false`,
   * `result.code` y, si el SAT la rechazó, su motivo en `result.rejectionReason`.
   */
  runCiec(clientId: string, ciec?: string): Observable<IResponseWithCode> {
    const response: IResponseWithCode = { error: true, msg: '', data: null }
    return this.http.post<IResponse>(`${environment.apiUrl}clients/${clientId}/verify-ciec`, ciec ? { ciec } : {}).pipe(
      map((r) => {
        response.data = r.data ?? r
        response.msg = r.msg ?? ''
        response.error = r.error ?? false
        return response
      }),
      catchError((e) => {
        response.msg = e?.error?.message ?? e?.error?.msg ?? 'No se pudo ejecutar la verificación CIEC'
        response.code = e?.error?.code
        response.data = null
        response.error = true
        return of(response)
      }),
    )
  }

  /**
   * POST /clients/:id/ciec-request — generar magic link CIEC (ADR-0032).
   * Manda el correo y devuelve `{ requestId, link, expiresAt, sentTo }` para
   * que el modal exponga el link copiable.
   *
   * B-075: el `code` del error viaja para que el modal diga qué pasó y qué sigue —400 `RFC_MISSING`/
   * `RFC_INVALID` (se corrige editando el cliente) y 409 `CIEC_REQUEST_IN_PROGRESS` (el cliente está
   * enviando su CIEC en ese momento)—. El 400 «sin correo» no trae código.
   */
  createCiecRequest(clientId: string): Observable<IResponseWithCode> {
    const response: IResponseWithCode = { error: true, msg: '', data: null }
    return this.http.post<IResponse>(`${environment.apiUrl}clients/${clientId}/ciec-request`, {}).pipe(
      map((r) => {
        response.data = r.data ?? r
        response.msg = r.msg ?? ''
        response.error = r.error ?? false
        return response
      }),
      catchError((e) => {
        response.msg = e?.error?.message ?? e?.error?.msg ?? 'No se pudo generar la solicitud de CIEC'
        response.code = e?.error?.code
        response.data = null
        response.error = true
        return of(response)
      }),
    )
  }

  /**
   * GET /clients/:id/ciec-requests — historial de magic links del cliente, recientes primero
   * (cualquier estado: pending, used, expired, cancelled, exhausted). Cada fila trae su `outcome` ya
   * calculado por el servidor y sus intentos: `data` es `CiecRequestListItem[]`
   * (`@data/interfaces/ciec-request.interface`).
   */
  listCiecRequests(clientId: string): Observable<IResponse> {
    const response: IResponse = { error: true, msg: '', data: null }
    return this.http.get<IResponse>(`${environment.apiUrl}clients/${clientId}/ciec-requests`).pipe(
      map((r) => {
        response.data = r.data ?? r
        response.msg = r.msg ?? ''
        response.error = r.error ?? false
        return response
      }),
      catchError((e) => {
        response.msg = e?.error?.message ?? e?.error?.msg ?? 'No se pudo cargar el historial de solicitudes'
        response.data = null
        response.error = true
        return of(response)
      }),
    )
  }

  /** DELETE /clients/:id/ciec-requests/:requestId — cancelar magic link `pending`. */
  cancelCiecRequest(clientId: string, requestId: string): Observable<IResponse> {
    const response: IResponse = { error: true, msg: '', data: null }
    return this.http.delete<IResponse>(`${environment.apiUrl}clients/${clientId}/ciec-requests/${requestId}`).pipe(
      map((r) => {
        response.data = r.data ?? r
        response.msg = r.msg ?? ''
        response.error = r.error ?? false
        return response
      }),
      catchError((e) => {
        response.msg = e?.error?.message ?? e?.error?.msg ?? 'No se pudo cancelar la solicitud'
        response.data = null
        response.error = true
        return of(response)
      }),
    )
  }

  /**
   * POST /clients/:id/ciec-requests/:requestId/resend — regenera y reenvía.
   * Como el token plano no se persiste (solo SHA-256), internamente equivale a
   * cancelar + crear nuevo; la response trae el nuevo `link`. Delega en el mismo servicio que
   * `createCiecRequest`, así que devuelve los mismos códigos de error. Solo aplica a solicitudes
   * `pending` o `expired`; el resto responde 400 (el modal no ofrece el botón ahí, `canResend`).
   */
  resendCiecRequest(clientId: string, requestId: string): Observable<IResponseWithCode> {
    const response: IResponseWithCode = { error: true, msg: '', data: null }
    return this.http.post<IResponse>(`${environment.apiUrl}clients/${clientId}/ciec-requests/${requestId}/resend`, {}).pipe(
      map((r) => {
        response.data = r.data ?? r
        response.msg = r.msg ?? ''
        response.error = r.error ?? false
        return response
      }),
      catchError((e) => {
        response.msg = e?.error?.message ?? e?.error?.msg ?? 'No se pudo reenviar la solicitud'
        response.code = e?.error?.code
        response.data = null
        response.error = true
        return of(response)
      }),
    )
  }

  /**
   * GET /clients/:id/verifications — historial. Disponible para uso futuro;
   * la UI v1 solo muestra la vigente (snapshot decisión del usuario).
   */
  getHistory(clientId: string): Observable<IResponse> {
    const response: IResponse = { error: true, msg: '', data: null }
    return this.http.get<IResponse>(`${environment.apiUrl}clients/${clientId}/verifications`).pipe(
      map((r) => {
        response.data = r.data ?? r
        response.msg = r.msg ?? ''
        response.error = r.error ?? false
        return response
      }),
      catchError((e) => {
        response.msg = e?.error?.msg ?? 'No se pudo cargar el historial'
        response.data = null
        response.error = true
        return of(response)
      }),
    )
  }
}
